'use client';

import { useState } from 'react'
import Link from 'next/link'
import { motion, AnimatePresence } from 'framer-motion'
import { 
  ArrowRight, ShoppingBag, Sparkles, X, CreditCard, 
  Download, ShieldCheck, Mail, CheckCircle2, DollarSign, Gift,
  Plus, Trash2, Power, Search, TrendingUp, Clock, ArrowUpRight, 
  Store, Inbox, Eye, Activity, Database
} from 'lucide-react'
import { GeometricBackground } from '@/components/ui/geometric-background'
import { MarketingNavbar } from '@/components/layout/marketing-navbar'
import { MarketingFooter } from '@/components/layout/marketing-footer'
import { toast } from 'sonner'

interface DashboardProduct {
  id: string
  title: string
  category: string
  price: number
  description: string
  imageBg: string
  is_active: boolean
  is_waitlist: boolean
  sales_count: number
  earnings: number
  file_name: string
}

interface StoreTransaction {
  id: string
  productTitle: string
  customerEmail: string
  amount: number
  date: string
  status: 'Completed' | 'Pending'
}

export default function DigitalProductStoreClient() {
  const [activeTab, setActiveTab] = useState<'dashboard' | 'storefront' | 'earnings'>('dashboard')
  const [searchQuery, setSearchQuery] = useState('')
  const [isAddModalOpen, setIsAddModalOpen] = useState(false)

  // Product states
  const [products, setProducts] = useState<DashboardProduct[]>([
    {
      id: 'prod-figma',
      title: 'Aesthetic Figma UI Kit',
      category: 'Design Template',
      price: 19.00,
      description: '150+ premium dark mode components & screens optimized for mobile & SaaS landing pages.',
      imageBg: 'from-blue-600 to-indigo-700',
      is_active: true,
      is_waitlist: false,
      sales_count: 84,
      earnings: 1596.00,
      file_name: 'aesthetic_figma_ui_kit_v1.2.fig'
    },
    {
      id: 'prod-ebook',
      title: 'The Creator Blueprint eBook',
      category: 'E-Book PDF',
      price: 9.00,
      description: 'A 120-page comprehensive strategy guide outlining how to scale digital products to $10k/mo.',
      imageBg: 'from-violet-600 to-fuchsia-700',
      is_active: true,
      is_waitlist: false,
      sales_count: 243,
      earnings: 2187.00,
      file_name: 'creator_blueprint_guide.pdf'
    },
    {
      id: 'prod-boilerplate',
      title: 'SaaS Starter Boilerplate',
      category: 'Codebase Starter',
      price: 49.00,
      description: 'Ready-to-deploy Next.js 15 template integrated with database schema, Clerk Auth & Stripe payouts.',
      imageBg: 'from-emerald-600 to-teal-700',
      is_active: true,
      is_waitlist: false,
      sales_count: 38,
      earnings: 1862.00,
      file_name: 'next15_saas_starter_boilerplate.zip'
    }
  ])

  // Transaction state
  const [transactions, setTransactions] = useState<StoreTransaction[]>([
    { id: 'tx-8921', productTitle: 'Aesthetic Figma UI Kit', customerEmail: 'tony@starkindustries.com', amount: 19.00, date: '2026-06-08 14:22', status: 'Completed' },
    { id: 'tx-7742', productTitle: 'SaaS Starter Boilerplate', customerEmail: 'peter.parker@midtownhigh.edu', amount: 49.00, date: '2026-06-08 11:05', status: 'Completed' },
    { id: 'tx-6109', productTitle: 'The Creator Blueprint eBook', customerEmail: 'bruce.banner@avengers.org', amount: 9.00, date: '2026-06-07 18:47', status: 'Completed' },
    { id: 'tx-5401', productTitle: 'Aesthetic Figma UI Kit', customerEmail: 'selina.kyle@gotham.city', amount: 19.00, date: '2026-06-07 09:12', status: 'Completed' }
  ])

  // Add Product Form states
  const [newTitle, setNewTitle] = useState('')
  const [newCategory, setNewCategory] = useState('Design Template')
  const [newPrice, setNewPrice] = useState('15.00')
  const [newDesc, setNewDesc] = useState('')
  const [newFile, setNewFile] = useState('')
  const [newIsWaitlist, setNewIsWaitlist] = useState(false)
  const [newBg, setNewBg] = useState('from-blue-600 to-indigo-700')

  // Checkout states
  const [selectedProduct, setSelectedProduct] = useState<DashboardProduct | null>(null)
  const [checkoutStep, setCheckoutStep] = useState<'form' | 'processing' | 'success'>('form')
  const [email, setEmail] = useState('')
  const [cardNumber, setCardNumber] = useState('')

  // Toggle handlers
  const handleToggleActive = (id: string) => {
    setProducts(prev => prev.map(p => {
      if (p.id === id) {
        const nextState = !p.is_active
        toast.success(`Product "${p.title}" is now ${nextState ? 'visible' : 'hidden'} on storefront`)
        return { ...p, is_active: nextState }
      }
      return p
    }))
  }

  const handleToggleWaitlist = (id: string) => {
    setProducts(prev => prev.map(p => {
      if (p.id === id) {
        const nextState = !p.is_waitlist
        toast.success(`Product "${p.title}" set to ${nextState ? 'Waitlist Mode' : 'Instant Purchase Mode'}`)
        return { ...p, is_waitlist: nextState }
      }
      return p
    }))
  }

  const handleDeleteProduct = (id: string) => {
    setProducts(prev => prev.filter(p => p.id !== id))
    toast.success('Product removed from store dashboard')
  }

  // Create Product handler
  const handleCreateProduct = (e: React.FormEvent) => {
    e.preventDefault()
    if (!newTitle.trim()) {
      toast.error('Product title is required')
      return
    }
    const priceVal = parseFloat(newPrice)
    if (isNaN(priceVal) || priceVal < 0) {
      toast.error('Please enter a valid price')
      return
    }

    const newProd: DashboardProduct = {
      id: `prod-${Date.now()}`,
      title: newTitle,
      category: newCategory,
      price: priceVal,
      description: newDesc || 'No description provided.',
      imageBg: newBg,
      is_active: true,
      is_waitlist: newIsWaitlist,
      sales_count: 0,
      earnings: 0,
      file_name: newFile || `${newTitle.toLowerCase().replace(/[^a-z0-9]/g, '_')}_file.zip`
    }

    setProducts(prev => [...prev, newProd])
    toast.success(`Product "${newTitle}" added to dashboard successfully!`)
    setIsAddModalOpen(false)

    // Reset fields
    setNewTitle('')
    setNewPrice('15.00')
    setNewDesc('')
    setNewFile('')
    setNewIsWaitlist(false)
  }

  // Checkout simulation handlers
  const handleOpenCheckout = (product: DashboardProduct) => {
    setSelectedProduct(product)
    setCheckoutStep('form')
    setEmail('')
    setCardNumber('')
  }

  const handleCheckoutSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (!email) {
      toast.error('Please enter a valid email address')
      return
    }

    if (selectedProduct?.is_waitlist) {
      // Waitlist Flow
      setCheckoutStep('processing')
      setTimeout(() => {
        setCheckoutStep('success')
        toast.success('Waitlist registration simulated successfully!')
      }, 1500)
      return
    }

    // Purchase Flow
    if (cardNumber.replace(/\s/g, '').length < 16) {
      toast.error('Please enter a valid card number')
      return
    }

    setCheckoutStep('processing')
    setTimeout(() => {
      setCheckoutStep('success')
      
      // Update statistics
      if (selectedProduct) {
        setProducts(prev => prev.map(p => {
          if (p.id === selectedProduct.id) {
            return {
              ...p,
              sales_count: p.sales_count + 1,
              earnings: p.earnings + p.price
            }
          }
          return p
        }))

        // Log transaction
        const newTx: StoreTransaction = {
          id: `tx-${Math.floor(Math.random() * 9000) + 1000}`,
          productTitle: selectedProduct.title,
          customerEmail: email,
          amount: selectedProduct.price,
          date: new Date().toISOString().replace('T', ' ').slice(0, 16),
          status: 'Completed'
        }
        setTransactions(prev => [newTx, ...prev])
      }

      toast.success('Simulation payment processed successfully!')
    }, 2000)
  }

  const handleDownloadFile = () => {
    if (!selectedProduct) return
    const content = `Thank you for purchasing ${selectedProduct.title}!\n\nThis is a mock digital file delivery simulation.\nOrder: #${Math.floor(Math.random() * 90000) + 10000}\nTransaction ID: tx_${Math.random().toString(36).substring(2, 12)}`
    const blob = new Blob([content], { type: 'text/plain' })
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = selectedProduct.file_name
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    URL.revokeObjectURL(url)
    
    toast.success('Digital file downloaded successfully!')
  }

  // Calculations for Earnings
  const totalSales = products.reduce((acc, p) => acc + p.sales_count, 0)
  const totalRevenue = products.reduce((acc, p) => acc + p.earnings, 0)
  const availableBalance = totalRevenue * 0.85 // Simulate payout balance

  const filteredProducts = products.filter(p => 
    p.title.toLowerCase().includes(searchQuery.toLowerCase()) || 
    p.category.toLowerCase().includes(searchQuery.toLowerCase())
  )

  const activeStorefrontProducts = products.filter(p => p.is_active)

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: { opacity: 1, transition: { staggerChildren: 0.15 } }
  }
  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.6 } }
  }

  return (
    <div className="relative min-h-screen bg-black overflow-x-clip selection:bg-primary/30 text-white">
      <GeometricBackground />
      <MarketingNavbar />

      {/* Hero Section */}
      <section className="relative pt-32 pb-16 px-6 max-w-[1400px] mx-auto z-10 flex flex-col items-center">
        <motion.div 
          className="text-center space-y-6 max-w-3xl"
          variants={containerVariants}
          initial="hidden"
          animate="visible"
        >
          <motion.div variants={itemVariants} className="flex items-center gap-2 justify-center">
            <span className="px-3 py-1 bg-primary/20 text-primary text-[10px] font-bold tracking-widest uppercase rounded-full">
              Gumroad Alternative
            </span>
          </motion.div>

          <motion.h1 variants={itemVariants} className="text-4xl sm:text-5xl lg:text-7xl font-bold tracking-tighter uppercase leading-[1.1]">
            Sell Digital Products Instantly<br />
            <span className="text-gray-500">Creator Storefronts with</span><br />
            Minimal Platform Fees
          </motion.h1>

          <motion.p variants={itemVariants} className="text-base sm:text-lg text-white/50 font-light max-w-xl mx-auto leading-relaxed">
            Sell templates, eBooks, design assets, and source codes direct to customers. Build a high-converting digital product store without coding.
          </motion.p>
        </motion.div>

        {/* Interactive Sandbox Dashboard Container */}
        <motion.div 
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4, duration: 0.8 }}
          className="w-full max-w-5xl mt-12 bg-[#0A0A0A]/80 border border-white/10 rounded-3xl backdrop-blur-xl shadow-[0_0_80px_rgba(0,102,255,0.05)] overflow-hidden"
        >
          {/* Dashboard Header Bar */}
          <div className="px-6 py-4 border-b border-white/[0.08] flex flex-col sm:flex-row items-start sm:items-center justify-between bg-white/[0.02] gap-4">
            <div className="flex items-center gap-2">
              <Store className="w-4 h-4 text-primary" />
              <span className="text-sm font-semibold uppercase tracking-wider">Merchant Store Workspace</span>
            </div>
            
            {activeTab === 'dashboard' && (
              <button
                onClick={() => setIsAddModalOpen(true)}
                className="flex items-center gap-1.5 px-4 py-2 bg-primary hover:bg-white text-white hover:text-black rounded-xl text-xs font-bold transition-all duration-300"
              >
                <Plus className="w-3.5 h-3.5" /> Add Product
              </button>
            )}
          </div>

          {/* Workspace Tabs Navigation */}
          <div className="flex border-b border-white/[0.08] px-4 pt-3 bg-white/[0.01]">
            {[
              { id: 'dashboard', label: 'Products Dashboard' },
              { id: 'storefront', label: 'Customer Storefront' },
              { id: 'earnings', label: 'Store Earnings' }
            ].map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id as any)}
                className={`flex-1 pb-3 text-[10px] sm:text-xs font-bold uppercase tracking-wider transition-all border-b-2 ${
                  activeTab === tab.id 
                    ? 'text-white border-primary' 
                    : 'text-white/40 border-transparent hover:text-white/60'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>

          {/* Tab Workspace Panels */}
          <div className="p-6">
            <AnimatePresence mode="wait">

              {/* ════ PRODUCTS DASHBOARD VIEW ════ */}
              {activeTab === 'dashboard' && (
                <motion.div
                  key="products-dashboard-tab"
                  initial={{ opacity: 0, y: 5 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -5 }}
                  className="space-y-6"
                >
                  <div className="relative max-w-md">
                    <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-white/25" />
                    <input
                      type="text"
                      placeholder="Search store products..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="w-full pl-11 pr-4 py-2.5 bg-white/[0.04] border border-white/[0.06] rounded-xl text-xs text-white placeholder-white/25 focus:outline-none"
                    />
                  </div>

                  <div className="overflow-x-auto border border-white/[0.06] rounded-2xl bg-white/[0.01]">
                    <table className="w-full min-w-[700px] border-collapse text-left text-xs text-white/70">
                      <thead>
                        <tr className="border-b border-white/[0.08] bg-white/[0.02] text-[10px] font-mono uppercase tracking-wider text-white/40">
                          <th className="px-5 py-4">Product Details</th>
                          <th className="px-5 py-4">Price</th>
                          <th className="px-5 py-4">File Delivery</th>
                          <th className="px-5 py-4">Sales / Rev</th>
                          <th className="px-5 py-4 text-center">Settings</th>
                          <th className="px-5 py-4 text-right">Action</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredProducts.map((prod) => (
                          <tr key={prod.id} className="border-b border-white/[0.04] hover:bg-white/[0.02] transition-all">
                            <td className="px-5 py-4">
                              <div className="flex items-center gap-3">
                                <div className={`w-10 h-10 rounded-lg bg-gradient-to-br ${prod.imageBg} flex items-center justify-center text-white/80 shrink-0 font-bold text-xs`}>
                                  <ShoppingBag className="w-4 h-4" />
                                </div>
                                <div>
                                  <div className="font-bold text-white text-sm">{prod.title}</div>
                                  <div className="text-[10px] text-white/40">{prod.category}</div>
                                </div>
                              </div>
                            </td>
                            <td className="px-5 py-4 font-mono text-white font-semibold">
                              ${prod.price.toFixed(2)}
                            </td>
                            <td className="px-5 py-4">
                              <div className="flex items-center gap-1.5 text-white/50">
                                <Database className="w-3.5 h-3.5 text-primary" />
                                <span className="truncate max-w-[120px] font-mono text-[10px]" title={prod.file_name}>
                                  {prod.file_name}
                                </span>
                              </div>
                            </td>
                            <td className="px-5 py-4">
                              <div>
                                <div className="font-bold text-white font-mono">{prod.sales_count} sales</div>
                                <div className="text-[10px] text-primary font-mono">${prod.earnings.toFixed(2)}</div>
                              </div>
                            </td>
                            <td className="px-5 py-4">
                              <div className="flex flex-col gap-2 items-center justify-center">
                                <button
                                  onClick={() => handleToggleActive(prod.id)}
                                  className={`flex items-center gap-1 px-2.5 py-1 rounded-full text-[9px] font-bold transition-all border ${
                                    prod.is_active 
                                      ? 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20' 
                                      : 'bg-zinc-800 text-white/40 border-white/5'
                                  }`}
                                >
                                  <Power className="w-2.5 h-2.5" /> {prod.is_active ? 'Active' : 'Hidden'}
                                </button>
                                
                                <button
                                  onClick={() => handleToggleWaitlist(prod.id)}
                                  className={`flex items-center gap-1 px-2.5 py-1 rounded-full text-[9px] font-bold transition-all border ${
                                    prod.is_waitlist 
                                      ? 'bg-amber-500/10 text-amber-400 border-amber-500/20' 
                                      : 'bg-indigo-500/10 text-indigo-400 border-indigo-500/20'
                                  }`}
                                >
                                  <Activity className="w-2.5 h-2.5" /> {prod.is_waitlist ? 'Waitlist' : 'Buy Now'}
                                </button>
                              </div>
                            </td>
                            <td className="px-5 py-4 text-right">
                              <button
                                onClick={() => handleDeleteProduct(prod.id)}
                                className="p-2 text-white/20 hover:text-red-400 hover:bg-red-500/10 rounded-xl transition-all"
                                title="Delete Product"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </td>
                          </tr>
                        ))}
                        {filteredProducts.length === 0 && (
                          <tr>
                            <td colSpan={6} className="px-5 py-8 text-center text-white/20 font-light">
                              No products found. Add a product to get started.
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </motion.div>
              )}

              {/* ════ CUSTOMER STOREFRONT VIEW ════ */}
              {activeTab === 'storefront' && (
                <motion.div
                  key="customer-storefront-tab"
                  initial={{ opacity: 0, y: 5 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -5 }}
                  className="space-y-6"
                >
                  <div className="flex justify-between items-center pb-2 border-b border-white/5">
                    <div>
                      <h3 className="text-sm font-bold text-white uppercase tracking-tight">Customer Live Storefront</h3>
                      <p className="text-[10px] text-white/40 mt-0.5">This is what visitors see at your custom links store url</p>
                    </div>
                    <div className="flex items-center gap-1 px-2.5 py-1 bg-white/[0.04] border border-white/[0.08] rounded-full text-[10px] text-white/60">
                      <div className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" /> Live Preview
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    {activeStorefrontProducts.map((prod) => (
                      <div 
                        key={prod.id}
                        className="group bg-white/[0.02] border border-white/5 rounded-3xl p-5 flex flex-col justify-between shadow-[0_0_50px_rgba(0,102,255,0.01)] hover:border-white/10 transition-all duration-300"
                      >
                        <div>
                          <div className={`w-full aspect-[16/10] rounded-2xl bg-gradient-to-br ${prod.imageBg} p-4 flex flex-col justify-between relative overflow-hidden`}>
                            <div className="absolute inset-0 bg-black/10 backdrop-blur-[1px]" />
                            <span className="px-2 py-0.5 bg-black/40 border border-white/10 rounded-full text-[8px] font-mono tracking-wider text-white uppercase relative z-10 w-fit">
                              {prod.category}
                            </span>
                            <div className="relative z-10 flex items-center justify-center h-full">
                              <ShoppingBag className="w-10 h-10 text-white/80 group-hover:scale-110 transition-transform duration-300" />
                            </div>
                          </div>

                          <div className="mt-4 space-y-2">
                            <div className="flex justify-between items-start gap-2">
                              <h3 className="text-sm font-bold uppercase tracking-tight text-white line-clamp-1">{prod.title}</h3>
                              <span className="text-sm font-mono font-bold text-primary">${prod.price.toFixed(2)}</span>
                            </div>
                            <p className="text-[11px] text-white/50 font-light leading-relaxed line-clamp-2">{prod.description}</p>
                          </div>
                        </div>

                        <button
                          onClick={() => handleOpenCheckout(prod)}
                          className={`w-full py-2.5 text-black font-bold rounded-2xl text-xs mt-5 transition-all active:scale-95 flex items-center justify-center gap-1 shadow-lg ${
                            prod.is_waitlist 
                              ? 'bg-amber-400 hover:bg-amber-300' 
                              : 'bg-white hover:bg-white/90'
                          }`}
                        >
                          {prod.is_waitlist ? 'Join Waitlist' : 'Buy Now'}
                        </button>
                      </div>
                    ))}
                    {activeStorefrontProducts.length === 0 && (
                      <div className="col-span-3 py-16 flex flex-col items-center justify-center text-center space-y-4 border border-dashed border-white/10 rounded-3xl">
                        <Inbox className="w-10 h-10 text-white/20" />
                        <div>
                          <p className="text-xs font-bold text-white">No active storefront products</p>
                          <p className="text-[10px] text-white/40 mt-1">Make sure you toggle product visibility settings in the admin dashboard</p>
                        </div>
                      </div>
                    )}
                  </div>
                </motion.div>
              )}

              {/* ════ STORE EARNINGS VIEW ════ */}
              {activeTab === 'earnings' && (
                <motion.div
                  key="store-earnings-tab"
                  initial={{ opacity: 0, y: 5 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -5 }}
                  className="space-y-6"
                >
                  <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
                    <div className="p-4 bg-white/[0.01] border border-white/5 rounded-2xl">
                      <p className="text-[9px] font-mono text-white/40 uppercase">Total Sales Volume</p>
                      <p className="text-lg font-black text-white font-mono mt-1">{totalSales}</p>
                    </div>
                    <div className="p-4 bg-white/[0.01] border border-white/5 rounded-2xl">
                      <p className="text-[9px] font-mono text-white/40 uppercase">Gross Revenue</p>
                      <p className="text-lg font-black text-primary font-mono mt-1">${totalRevenue.toFixed(2)}</p>
                    </div>
                    <div className="p-4 bg-white/[0.01] border border-white/5 rounded-2xl">
                      <p className="text-[9px] font-mono text-white/40 uppercase">Available Payout</p>
                      <p className="text-lg font-black text-emerald-400 font-mono mt-1">${availableBalance.toFixed(2)}</p>
                    </div>
                    <div className="p-4 bg-white/[0.01] border border-white/5 rounded-2xl flex flex-col justify-between">
                      <div>
                        <p className="text-[9px] font-mono text-white/40 uppercase">Payout Gateway</p>
                        <p className="text-xs font-bold text-white mt-1">Stripe Connected</p>
                      </div>
                      <div className="w-fit px-1.5 py-0.5 bg-emerald-500/10 text-emerald-400 rounded text-[8px] font-mono font-bold mt-1">
                        Active
                      </div>
                    </div>
                  </div>

                  {/* Transaction log */}
                  <div className="p-5 bg-white/[0.01] border border-white/5 rounded-2xl space-y-4">
                    <div className="flex items-center justify-between">
                      <p className="text-xs font-semibold text-white/80 flex items-center gap-1.5">
                        <TrendingUp className="w-4 h-4 text-primary" /> Recent Sales Logs
                      </p>
                      <span className="text-[9px] font-mono text-white/30">Sandbox transactions</span>
                    </div>

                    <div className="overflow-x-auto">
                      <table className="w-full border-collapse text-left text-[11px] text-white/60 font-mono">
                        <thead>
                          <tr className="border-b border-white/5 text-[9px] uppercase tracking-wider text-white/30">
                            <th className="py-2.5">TX ID</th>
                            <th className="py-2.5">Customer Email</th>
                            <th className="py-2.5">Purchased Item</th>
                            <th className="py-2.5">Amount</th>
                            <th className="py-2.5">Date</th>
                            <th className="py-2.5 text-right">Status</th>
                          </tr>
                        </thead>
                        <tbody>
                          {transactions.map((tx) => (
                            <tr key={tx.id} className="border-b border-white/[0.02] hover:bg-white/[0.01]">
                              <td className="py-2 text-white/80">{tx.id}</td>
                              <td className="py-2 text-white/40">{tx.customerEmail}</td>
                              <td className="py-2 text-white/80 font-sans">{tx.productTitle}</td>
                              <td className="py-2 text-primary font-bold">${tx.amount.toFixed(2)}</td>
                              <td className="py-2 text-white/30">{tx.date}</td>
                              <td className="py-2 text-right">
                                <span className={`px-1.5 py-0.5 rounded text-[8px] font-bold ${
                                  tx.status === 'Completed' 
                                    ? 'bg-emerald-500/10 text-emerald-400' 
                                    : 'bg-amber-500/10 text-amber-400'
                                }`}>
                                  {tx.status}
                                </span>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </motion.div>
              )}

            </AnimatePresence>
          </div>
        </motion.div>
      </section>

      {/* Add Product Modal Overlay */}
      <AnimatePresence>
        {isAddModalOpen && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <motion.div 
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setIsAddModalOpen(false)}
              className="absolute inset-0 bg-black/80 backdrop-blur-md"
            />
            
            <motion.div 
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              className="relative w-full max-w-lg bg-zinc-950 border border-white/10 rounded-3xl p-6 md:p-8 z-10 shadow-2xl overflow-y-auto max-h-[90vh]"
            >
              <button 
                onClick={() => setIsAddModalOpen(false)}
                className="absolute top-4 right-4 p-2 text-white/40 hover:text-white bg-white/5 rounded-full transition-colors"
                aria-label="Close modal"
              >
                <X className="w-4 h-4" />
              </button>

              <div className="space-y-6">
                <div>
                  <span className="text-[10px] font-mono text-primary uppercase tracking-widest block mb-1">Store Setup</span>
                  <h3 className="text-lg font-bold text-white uppercase tracking-tight">Add Digital Product</h3>
                  <p className="text-xs text-white/50 mt-1">Configure pricing, description and delivery assets for buyers</p>
                </div>

                <form onSubmit={handleCreateProduct} className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-1.5">
                      <label className="text-[10px] font-mono text-white/40 uppercase block">Product Name</label>
                      <input
                        type="text"
                        required
                        placeholder="e.g. Creator Guide PDF"
                        value={newTitle}
                        onChange={(e) => setNewTitle(e.target.value)}
                        className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-xl text-xs text-white focus:outline-none focus:border-primary/60 transition-all font-light"
                      />
                    </div>

                    <div className="space-y-1.5">
                      <label className="text-[10px] font-mono text-white/40 uppercase block">Category</label>
                      <select
                        value={newCategory}
                        onChange={(e) => setNewCategory(e.target.value)}
                        className="w-full px-3 py-2 bg-zinc-900 border border-white/10 rounded-xl text-xs text-white focus:outline-none focus:border-primary/60 transition-all font-light"
                      >
                        <option value="Design Template">Design Template</option>
                        <option value="E-Book PDF">E-Book PDF</option>
                        <option value="Codebase Starter">Codebase Starter</option>
                        <option value="Other">Other</option>
                      </select>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-1.5">
                      <label className="text-[10px] font-mono text-white/40 uppercase block">Price ($ USD)</label>
                      <input
                        type="number"
                        step="0.01"
                        required
                        placeholder="0.00"
                        value={newPrice}
                        onChange={(e) => setNewPrice(e.target.value)}
                        className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-xl text-xs text-white focus:outline-none focus:border-primary/60 transition-all font-mono"
                      />
                    </div>

                    <div className="space-y-1.5">
                      <label className="text-[10px] font-mono text-white/40 uppercase block">Asset File Name</label>
                      <input
                        type="text"
                        placeholder="e.g. guide.pdf, starter.zip"
                        value={newFile}
                        onChange={(e) => setNewFile(e.target.value)}
                        className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-xl text-xs text-white focus:outline-none focus:border-primary/60 transition-all font-mono"
                      />
                    </div>
                  </div>

                  <div className="space-y-1.5">
                    <label className="text-[10px] font-mono text-white/40 uppercase block">Description</label>
                    <textarea
                      rows={3}
                      placeholder="Brief details about files, templates, or instructions included..."
                      value={newDesc}
                      onChange={(e) => setNewDesc(e.target.value)}
                      className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-xl text-xs text-white focus:outline-none focus:border-primary/60 transition-all font-light resize-none"
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4 items-center">
                    <div className="space-y-1.5">
                      <label className="text-[10px] font-mono text-white/40 uppercase block">Card Cover Color</label>
                      <select
                        value={newBg}
                        onChange={(e) => setNewBg(e.target.value)}
                        className="w-full px-3 py-2 bg-zinc-900 border border-white/10 rounded-xl text-xs text-white focus:outline-none focus:border-primary/60 transition-all font-light"
                      >
                        <option value="from-blue-600 to-indigo-700">Aesthetic Blue</option>
                        <option value="from-violet-600 to-fuchsia-700">Deep Purple</option>
                        <option value="from-emerald-600 to-teal-700">Emerald Green</option>
                        <option value="from-amber-600 to-orange-700">Warm Orange</option>
                      </select>
                    </div>

                    <div className="flex items-center justify-between bg-white/[0.02] border border-white/5 p-3 rounded-xl mt-4">
                      <div>
                        <span className="text-[10px] font-mono text-white block uppercase">Waitlist Mode</span>
                        <span className="text-[8px] text-white/40 block">Accept waitlist emails</span>
                      </div>
                      <input
                        type="checkbox"
                        checked={newIsWaitlist}
                        onChange={(e) => setNewIsWaitlist(e.target.checked)}
                        className="w-4 h-4 accent-primary rounded bg-white/5 border-white/10"
                      />
                    </div>
                  </div>

                  <button
                    type="submit"
                    className="w-full py-3 bg-primary text-white font-bold rounded-2xl text-xs mt-4 transition-all hover:bg-primary/95 shadow-[0_0_20px_rgba(0,102,255,0.3)] active:scale-95"
                  >
                    Create Product & Publish
                  </button>
                </form>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* Stripe Checkout Modal Overlay */}
      <AnimatePresence>
        {selectedProduct && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <motion.div 
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setSelectedProduct(null)}
              className="absolute inset-0 bg-black/80 backdrop-blur-md"
            />

            <motion.div 
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              className="relative w-full max-w-md bg-zinc-950 border border-white/10 rounded-3xl p-6 md:p-8 z-10 shadow-2xl overflow-hidden"
            >
              <button 
                onClick={() => setSelectedProduct(null)}
                className="absolute top-4 right-4 p-2 text-white/40 hover:text-white bg-white/5 rounded-full transition-colors"
                aria-label="Close modal"
              >
                <X className="w-4 h-4" />
              </button>

              {checkoutStep === 'form' && (
                <div className="space-y-6">
                  <div>
                    <span className="text-[10px] font-mono text-primary uppercase tracking-widest block mb-1">
                      {selectedProduct.is_waitlist ? 'Waitlist Signup' : 'Checkout Sandbox'}
                    </span>
                    <h3 className="text-lg font-bold text-white uppercase tracking-tight">{selectedProduct.title}</h3>
                    <p className="text-xs text-white/50 mt-1">
                      {selectedProduct.is_waitlist 
                        ? 'Submit your email to request early invitation' 
                        : `$${selectedProduct.price.toFixed(2)} • Secure simulated billing`}
                    </p>
                  </div>

                  <form onSubmit={handleCheckoutSubmit} className="space-y-4">
                    <div className="space-y-1.5">
                      <label htmlFor="checkout-email" className="text-[10px] font-mono text-white/40 uppercase block">Deliver to Email</label>
                      <div className="relative">
                        <Mail className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-white/30" />
                        <input
                          id="checkout-email"
                          type="email"
                          required
                          placeholder="you@domain.com"
                          value={email}
                          onChange={(e) => setEmail(e.target.value)}
                          className="w-full pl-10 pr-4 py-3 bg-white/5 border border-white/10 rounded-xl text-xs focus:outline-none focus:border-primary/60 transition-all font-light text-white"
                        />
                      </div>
                    </div>

                    {!selectedProduct.is_waitlist && (
                      <>
                        <div className="space-y-1.5">
                          <label htmlFor="checkout-card" className="text-[10px] font-mono text-white/40 uppercase block">Mock Card Number</label>
                          <div className="relative">
                            <CreditCard className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-white/30" />
                            <input
                              id="checkout-card"
                              type="text"
                              required
                              maxLength={19}
                              placeholder="4242 4242 4242 4242"
                              value={cardNumber}
                              onChange={(e) => {
                                const val = e.target.value.replace(/\s?/g, '').replace(/[^0-9]/g, '');
                                const matches = val.match(/\d{1,4}/g);
                                const matchStr = matches ? matches.join(' ') : '';
                                setCardNumber(matchStr);
                              }}
                              className="w-full pl-10 pr-4 py-3 bg-white/5 border border-white/10 rounded-xl text-xs focus:outline-none focus:border-primary/60 transition-all font-mono text-white"
                            />
                          </div>
                        </div>

                        <div className="grid grid-cols-2 gap-3">
                          <div className="space-y-1.5">
                            <label className="text-[10px] font-mono text-white/40 uppercase block">Expiry</label>
                            <input
                              type="text"
                              required
                              maxLength={5}
                              placeholder="MM/YY"
                              className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-xs focus:outline-none focus:border-primary/60 transition-all font-mono text-white"
                            />
                          </div>
                          <div className="space-y-1.5">
                            <label className="text-[10px] font-mono text-white/40 uppercase block">CVC</label>
                            <input
                              type="password"
                              required
                              maxLength={3}
                              placeholder="•••"
                              className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-xs focus:outline-none focus:border-primary/60 transition-all font-mono text-white"
                            />
                          </div>
                        </div>
                      </>
                    )}

                    <button
                      id="submit-payment"
                      type="submit"
                      className="w-full py-3.5 bg-primary text-white font-bold rounded-2xl text-xs mt-4 transition-all hover:bg-primary/95 shadow-[0_0_20px_rgba(0,102,255,0.3)] active:scale-95"
                    >
                      {selectedProduct.is_waitlist 
                        ? 'Join Waitlist Notification' 
                        : `Authorize Payment of $${selectedProduct.price.toFixed(2)}`}
                    </button>
                  </form>
                </div>
              )}

              {checkoutStep === 'processing' && (
                <div className="py-12 flex flex-col items-center justify-center text-center space-y-4">
                  <div className="relative w-12 h-12">
                    <div className="absolute inset-0 border-4 border-white/10 rounded-full" />
                    <div className="absolute inset-0 border-4 border-t-primary rounded-full animate-spin" />
                  </div>
                  <div className="space-y-1">
                    <p className="text-sm font-semibold text-white">
                      {selectedProduct.is_waitlist ? 'Adding to Waitlist...' : 'Validating transaction...'}
                    </p>
                    <p className="text-[10px] text-white/40 font-mono">Securing gateway handshake</p>
                  </div>
                </div>
              )}

              {checkoutStep === 'success' && (
                <div className="py-6 flex flex-col items-center justify-center text-center space-y-6">
                  <div className="w-16 h-16 rounded-full bg-emerald-500/10 border border-emerald-500/30 flex items-center justify-center text-emerald-400">
                    <CheckCircle2 className="w-8 h-8" />
                  </div>
                  
                  <div className="space-y-2">
                    <h4 className="text-xl font-bold uppercase tracking-tight text-white">
                      {selectedProduct.is_waitlist ? 'Waitlist Confirmed!' : 'Checkout Success!'}
                    </h4>
                    <p className="text-xs text-white/50 max-w-[280px]">
                      {selectedProduct.is_waitlist 
                        ? `You have joined the waitlist for ${selectedProduct.title}. We will alert you soon.`
                        : `Your mock transaction for $${selectedProduct.price.toFixed(2)} was simulated successfully.`}
                    </p>
                  </div>

                  <div className="p-4 bg-white/[0.02] border border-white/5 rounded-2xl w-full text-left space-y-2">
                    <div className="flex justify-between text-[10px] font-mono">
                      <span className="text-white/40 uppercase">Delivery Email</span>
                      <span className="text-white/80">{email}</span>
                    </div>
                    {!selectedProduct.is_waitlist && (
                      <div className="flex justify-between text-[10px] font-mono">
                        <span className="text-white/40 uppercase">Receipt Code</span>
                        <span className="text-white/80">#LST-{Math.floor(Math.random()*90000)+10000}</span>
                      </div>
                    )}
                  </div>

                  {!selectedProduct.is_waitlist ? (
                    <button
                      id="download-asset-btn"
                      onClick={handleDownloadFile}
                      className="w-full py-3 bg-white text-black font-bold rounded-2xl text-xs hover:bg-white/90 transition-all flex items-center justify-center gap-1.5 active:scale-95"
                    >
                      <Download className="w-4 h-4" /> Download Digital File
                    </button>
                  ) : (
                    <button
                      onClick={() => setSelectedProduct(null)}
                      className="w-full py-3 bg-white text-black font-bold rounded-2xl text-xs hover:bg-white/90 transition-all flex items-center justify-center gap-1.5 active:scale-95"
                    >
                      Done
                    </button>
                  )}
                </div>
              )}
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* Features */}
      <section className="py-20 border-t border-white/5 relative z-10">
        <div className="max-w-[1400px] mx-auto px-6 grid grid-cols-1 md:grid-cols-3 gap-8">
          <div className="p-6 rounded-2xl bg-white/[0.02] border border-white/5 space-y-3">
            <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
              <DollarSign className="w-5 h-5 text-primary" />
            </div>
            <h3 className="text-lg font-semibold uppercase tracking-tight">Ultra-Low Platform Fees</h3>
            <p className="text-sm text-white/50 font-light leading-relaxed">
              Tired of Gumroad charging 10% platform cuts? Keep more of your earnings with our minimal, ultra-low 5% platform fee.
            </p>
          </div>
          <div className="p-6 rounded-2xl bg-white/[0.02] border border-white/5 space-y-3">
            <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
              <ShieldCheck className="w-5 h-5 text-primary" />
            </div>
            <h3 className="text-lg font-semibold uppercase tracking-tight">Instant File Delivery</h3>
            <p className="text-sm text-white/50 font-light leading-relaxed">
              Files are hosted securely on our global CDN. Customers receive encrypted unique download URLs instantly after completing checkout.
            </p>
          </div>
          <div className="p-6 rounded-2xl bg-white/[0.02] border border-white/5 space-y-3">
            <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
              <Gift className="w-5 h-5 text-primary" />
            </div>
            <h3 className="text-lg font-semibold uppercase tracking-tight">Discount Codes & Bundles</h3>
            <p className="text-sm text-white/50 font-light leading-relaxed">
              Create coupons, flash sales, or license keys. Bundle items together to boost Average Order Value (AOV) on your site.
            </p>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 border-t border-white/5 text-center relative z-10 overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,rgba(0,102,255,0.05),transparent_70%)] blur-[100px] pointer-events-none" />
        <h2 className="text-3xl sm:text-5xl font-bold uppercase tracking-tighter">Start selling today</h2>
        <p className="text-white/40 text-sm max-w-md mx-auto mt-4 font-light">Set up your digital product storefront, list your items, and accept credit card payments globally.</p>
        <Link 
          href="/auth/sign-up" 
          className="inline-flex items-center gap-2 mt-8 px-8 py-3.5 bg-primary text-white font-bold rounded-full hover:scale-105 transition-all shadow-[0_0_30px_rgba(0,102,255,0.3)] active:scale-95"
        >
          Create Creator Storefront <ArrowRight className="w-4 h-4" />
        </Link>
      </section>

      <MarketingFooter />
    </div>
  )
}
